import { useEffect, useState } from "react";

import { PayinQrCard } from "@/modules/dashboard/payins/components/PayinQrCard";
import { pixQrDataUrl } from "@/modules/dashboard/payins/utils/pixQrDataUrl";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import useTranslations from "@/shared/lang/useTranslations";
import type { BrebReceivingKey } from "@/shared/services/payments/brebReceivingKey.service";

import { useBrebKeyRangeLabel } from "./useBrebKeyRangeLabel";

interface RenderedQr {
  payload: string;
  src: string | null;
}

export interface BrebKeyStaticQrProps {
  receivingKey: BrebReceivingKey;
  className?: string;
}

/**
 * The key's any-amount QR, drawn from its own payload, or a placeholder while the backend is still minting
 * it. Nothing for a key that cannot be paid yet.
 */
export function BrebKeyStaticQr({ receivingKey, className }: BrebKeyStaticQrProps) {
  const { t } = useTranslations("payins");
  const range = useBrebKeyRangeLabel(receivingKey);
  const payload = receivingKey.status === "PENDING" ? null : receivingKey.static_qr_payload;
  const generating = receivingKey.status === "ACTIVE" && !receivingKey.static_qr_payload;
  const [rendered, setRendered] = useState<RenderedQr | null>(null);

  useEffect(() => {
    if (!payload) return;
    let cancelled = false;
    void pixQrDataUrl(payload).then((src) => {
      if (!cancelled) setRendered({ payload, src });
    });
    return () => {
      cancelled = true;
    };
  }, [payload]);

  if (payload) {
    return (
      <PayinQrCard
        className={className}
        src={rendered?.payload === payload ? rendered.src : null}
        rail="BREB"
        footer={range}
        shareExpiry={null}
        brebKey={receivingKey.key_value}
      />
    );
  }

  if (generating) {
    return (
      <div className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4">
        <ColocaSkeleton className="size-16 shrink-0 rounded-xl" />
        <p role="status" className="text-sm text-muted-foreground">
          {t("breb_key_static_qr_generating")}
        </p>
      </div>
    );
  }

  return null;
}
